/**
 * Surface 3 — Jurisdiction Collectors & Assignments Tab (Phase 3 UI).
 * See /app/memory/design_tax_collectors_ui.md §3 Surface 3.
 *
 * SCOPE (LOCKED, May 28, 2026 — Option B):
 *   This component is rendered ONLY for Pennsylvania jurisdictions.
 *   The caller (JurisdictionWizardModal) gates visibility on
 *   `jurisdiction.state === 'PA'`. Do NOT render this component for
 *   non-PA states — they remain on the legacy Jurisdiction Directory
 *   collector fields permanently per the Source-of-Truth Policy
 *   (see §0 of /app/memory/design_tax_collectors.md).
 *
 * Access:
 *   • admin / jurisdiction_manager → full CRUD
 *   • all other roles → read-only view
 *
 * Layout:
 *   Two stacked tables (Current / Delinquent) with their own [+ Add]
 *   button. Soft-delete (deactivate) on the row Trash icon. Inline
 *   non-blocking overlap warning on the Add/Edit modal.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, AlertTriangle, Loader2, ExternalLink, Search, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import {
  listJurisdictionAssignments,
  createJurisdictionAssignment,
  updateJurisdictionAssignment,
  deactivateJurisdictionAssignment,
  listTaxCollectors,
  listTaxCategories,
} from '../../utils/taxCollectorsApi';
import { defaultCategoryForPA } from '../../utils/jurisdictionCategory';
import { formatDateOnly } from '../../utils/formatters';

const WRITE_ROLES = new Set(['admin', 'jurisdiction_manager']);

// Feb 2026 (Option C) — jurisdiction types eligible as a
// municipality-scoped override for a scoped assignment. Mirrors
// `_SCOPE_ELIGIBLE_TYPES` in
// backend/routes/jurisdiction_collector_assignments.py.
const SCOPE_ELIGIBLE_TYPES = new Set([
  'township', 'borough', 'city', 'town', 'village',
]);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const emptyDraft = (jurisdictionId, taxPeriod) => ({
  jurisdiction_id: jurisdictionId,
  collector_id: '',
  tax_period: taxPeriod,
  tax_category_code: '',  // empty = all categories (NULL on backend)
  scope_jurisdiction_id: '',  // empty = all municipalities (NULL on backend)
  effective_from: new Date().toISOString().slice(0, 10),
  effective_to: '',
  priority: 0,
  notes: '',
  is_active: true,
});

function fmtDate(v) {
  if (!v) return '—';
  try {
    const d = typeof v === 'string' ? new Date(v) : v;
    if (Number.isNaN(d.getTime())) return '—';
    return formatDateOnly(d);
  } catch {
    return '—';
  }
}

function StatusPill({ active }) {
  if (active) {
    return (
      <span
        className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"
        data-testid="assignment-status-active"
      >
        Active
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600"
      data-testid="assignment-status-inactive"
    >
      Inactive
    </span>
  );
}

const PERIOD_LABELS = {
  current:    'Current Tax Collectors',
  delinquent: 'Delinquent Tax Collectors',
};

const JurisdictionCollectorAssignmentsTab = ({
  jurisdiction,
  currentUser,
}) => {
  const jurisdictionId = jurisdiction?.id;
  const isPA = (jurisdiction?.state || '').toUpperCase() === 'PA';
  const canWrite = useMemo(
    () => WRITE_ROLES.has(currentUser?.role),
    [currentUser?.role],
  );

  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [collectors, setCollectors] = useState([]);
  // COLLECTOR-PICKER-002 (Aug 2026): server-side searchable picker state.
  // Fixes the page_size=200 pagination cutoff (207 PA collectors in prod).
  const [picker, setPicker] = useState({ open: false, term: '', results: [], total: 0, searching: false });
  const [selectedCollector, setSelectedCollector] = useState(null);
  const [categories, setCategories] = useState([]);
  // Feb 2026 (Option C) — Municipalities eligible for the scope-override
  // dropdown. Fetched only for PA and only when the parent jurisdiction
  // has a `parent_county_id` (otherwise there's no sibling municipality
  // universe to pick from). Kept keyed by the assignment jurisdiction's
  // parent county so switching juris invalidates the cache.
  const [scopeCandidates, setScopeCandidates] = useState([]);
  const [loadingScopes, setLoadingScopes] = useState(false);

  // Add/Edit modal state
  const [modalState, setModalState] = useState({
    open: false,
    draft: null,
    editing: null,  // assignment.id when editing, null when adding
    saving: false,
    errors: {},
    overlap: [],
  });

  // PA catch-all confirmation gate. Surfaces when a PA user tries to save
  // an assignment with no explicit category. Non-blocking — they can still
  // confirm and proceed. See PA_ONBOARDING_CHECKLIST.md §Assignment Category
  // Standard for the policy this enforces.
  const [paCatchAllConfirm, setPaCatchAllConfirm] = useState({ open: false });

  // Soft-delete confirmation
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    target: null,
    submitting: false,
  });

  const refresh = useCallback(async () => {
    if (!jurisdictionId) return;
    setLoading(true);
    try {
      const [aRes, cRes, catRes] = await Promise.all([
        listJurisdictionAssignments(jurisdictionId),
        // Include inactive collectors so that EDIT-mode dropdowns can
        // still display (and re-select) a collector whose row was later
        // deactivated. Inactive entries are visually marked below.
        //
        // ⚠ Bug-fix May 30, 2026: omit `active_only` entirely instead of
        // passing `false`. The route uses a TRI-STATE filter
        // (null=all, true=active-only, false=INACTIVE-only). Passing
        // `false` returned only inactive collectors and hid newly-created
        // active ones from the dropdown. Omitting the key gives "all".
        listTaxCollectors({ state: 'PA', page_size: 200 }),
        listTaxCategories({ active_only: true }),
      ]);
      setAssignments(aRes?.items || []);
      setCollectors(cRes?.items || cRes?.collectors || []);
      setCategories(catRes?.items || catRes?.categories || []);
    } catch (err) {
      console.error('Failed to load assignments tab:', err);
      toast.error('Failed to load collector assignments');
    } finally {
      setLoading(false);
    }
  }, [jurisdictionId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Feb 2026 (Option C) — Populate the scope-override dropdown with the
  // sibling municipalities under the SAME parent county as this
  // jurisdiction. Only meaningful for PA jurisdictions that have a
  // parent county (school_district, township, etc.). Counties expose
  // their direct children so a county-level assignment can also be
  // scoped to a specific township.
  useEffect(() => {
    let cancelled = false;
    if (!isPA || !jurisdiction) {
      setScopeCandidates([]);
      return;
    }
    const jType = (jurisdiction.jurisdiction_type || '').toLowerCase();
    const parentId = jurisdiction.parent_county_id
      || (['county', 'parish', 'borough'].includes(jType) ? jurisdiction.id : null);
    if (!parentId || !jurisdiction.state) {
      setScopeCandidates([]);
      return;
    }
    (async () => {
      setLoadingScopes(true);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE}/api/jurisdictions`,
          {
            params: {
              state: jurisdiction.state,
              parent_county_id: parentId,
              page_size: 500,
              active_only: true,
            },
            headers: token ? { Authorization: `Bearer ${token}` } : undefined,
          },
        );
        if (cancelled) return;
        const rows = res?.data?.jurisdictions || res?.data?.items || [];
        const eligible = rows.filter((r) =>
          SCOPE_ELIGIBLE_TYPES.has((r.jurisdiction_type || '').toLowerCase())
          && r.id !== jurisdiction.id,
        );
        eligible.sort((a, b) =>
          (a.jurisdiction_name || '').localeCompare(b.jurisdiction_name || ''),
        );
        setScopeCandidates(eligible);
      } catch (err) {
        if (!cancelled) {
          console.error('Scope candidate fetch failed:', err);
          setScopeCandidates([]);
        }
      } finally {
        if (!cancelled) setLoadingScopes(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isPA, jurisdiction]);

  // Group assignments by tax_period (preserves table separation)
  const byPeriod = useMemo(() => {
    const out = { current: [], delinquent: [] };
    for (const a of assignments) {
      (out[a.tax_period] || (out[a.tax_period] = [])).push(a);
    }
    // Sort: active first, then priority asc, then effective_from desc
    for (const key of Object.keys(out)) {
      out[key].sort((x, y) => {
        if (x.is_active !== y.is_active) return x.is_active ? -1 : 1;
        if ((x.priority || 0) !== (y.priority || 0)) return (x.priority || 0) - (y.priority || 0);
        return (y.effective_from || '').localeCompare(x.effective_from || '');
      });
    }
    return out;
  }, [assignments]);

  const openAddModal = (taxPeriod) => {
    const draft = emptyDraft(jurisdictionId, taxPeriod);
    setSelectedCollector(null);
    setPicker((p) => ({ ...p, open: false, term: '' }));
    // PA standard: pre-select a category matching the jurisdiction's own
    // type so the default-of-least-resistance is the higher-confidence
    // explicit assignment, not the lower-confidence catch-all. The
    // catch-all entry remains selectable from the dropdown for the cases
    // where it's actually correct.
    if (isPA) {
      draft.tax_category_code = defaultCategoryForPA(
        jurisdiction?.jurisdiction_type,
        taxPeriod,
      );
    }
    setModalState({
      open: true,
      draft,
      editing: null,
      saving: false,
      errors: {},
      overlap: [],
    });
  };

  const openEditModal = (assignment) => {
    setSelectedCollector(assignment.collector || collectorById.get(assignment.collector_id) || null);
    setPicker((p) => ({ ...p, open: false, term: '' }));
    setModalState({
      open: true,
      draft: {
        jurisdiction_id: jurisdictionId,
        collector_id: assignment.collector_id || '',
        tax_period: assignment.tax_period,
        tax_category_code: assignment.tax_category_code || '',
        scope_jurisdiction_id: assignment.scope_jurisdiction_id || '',
        effective_from: (assignment.effective_from || '').slice(0, 10),
        effective_to: (assignment.effective_to || '').slice(0, 10) || '',
        priority: assignment.priority ?? 0,
        notes: assignment.notes || '',
        is_active: assignment.is_active !== false,
      },
      editing: assignment.id,
      saving: false,
      errors: {},
      overlap: [],
    });
  };

  const closeModal = () => {
    if (modalState.saving) return;
    setModalState({ open: false, draft: null, editing: null, saving: false, errors: {}, overlap: [] });
  };

  const setDraft = (patch) => {
    setModalState((s) => ({ ...s, draft: { ...s.draft, ...patch }, errors: {} }));
  };

  // COLLECTOR-PICKER-002: debounced SERVER-SIDE collector search. The state
  // filter (PA + nationwide) is applied by the backend; searching reaches
  // any collector regardless of which page it would fall on.
  useEffect(() => {
    if (!modalState.open) return undefined;
    let cancelled = false;
    setPicker((p) => ({ ...p, searching: true }));
    const t = setTimeout(async () => {
      try {
        const params = { state: 'PA', page_size: 50 };
        if (picker.term.trim()) params.search = picker.term.trim();
        const res = await listTaxCollectors(params);
        if (cancelled) return;
        setPicker((p) => ({
          ...p,
          results: res?.items || res?.collectors || [],
          total: res?.pagination?.total ?? (res?.items || []).length,
          searching: false,
        }));
      } catch {
        if (!cancelled) setPicker((p) => ({ ...p, results: [], total: 0, searching: false }));
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picker.term, modalState.open]);

  const validateDraft = (draft) => {
    const errs = {};
    if (!draft.collector_id) errs.collector_id = 'Collector is required';
    if (!draft.effective_from) errs.effective_from = 'Effective from is required';
    if (draft.effective_to && draft.effective_from && draft.effective_to < draft.effective_from) {
      errs.effective_to = 'Effective to must be on or after effective from';
    }
    if (draft.priority !== '' && draft.priority !== null && Number.isNaN(Number(draft.priority))) {
      errs.priority = 'Priority must be a number';
    }
    return errs;
  };

  const handleSave = async () => {
    const draft = modalState.draft;
    const errs = validateDraft(draft);
    if (Object.keys(errs).length) {
      setModalState((s) => ({ ...s, errors: errs }));
      return;
    }
    // PA-specific guard: nudge the user before saving a catch-all assignment
    // on a Pennsylvania jurisdiction. Non-blocking — they can confirm and
    // proceed. Catch-all on non-PA jurisdictions saves directly.
    if (isPA && !draft.tax_category_code) {
      setPaCatchAllConfirm({ open: true });
      return;
    }
    await performSave(draft);
  };

  const performSave = async (draft) => {
    setModalState((s) => ({ ...s, saving: true }));
    try {
      // Normalize payload
      const payload = {
        jurisdiction_id: jurisdictionId,
        collector_id: draft.collector_id,
        tax_period: draft.tax_period,
        tax_category_code: draft.tax_category_code || null,
        scope_jurisdiction_id: draft.scope_jurisdiction_id || null,
        effective_from: draft.effective_from,
        effective_to: draft.effective_to || null,
        priority: Number(draft.priority || 0),
        notes: draft.notes?.trim() || null,
        is_active: !!draft.is_active,
      };
      if (modalState.editing) {
        // PUT cannot change jurisdiction_id, omit it.
        const { jurisdiction_id, ...updatePayload } = payload;
        await updateJurisdictionAssignment(modalState.editing, updatePayload);
        toast.success('Assignment updated');
      } else {
        const res = await createJurisdictionAssignment(payload);
        const overlap = (res?.overlapping_assignments || []).filter(
          (x) => x.id !== res?.assignment?.id,
        );
        if (overlap.length) {
          // Surface inline non-blocking warning then close.
          toast.warning(
            `Saved. ${overlap.length} overlapping assignment(s) detected — resolver tiebreakers will apply.`,
          );
        } else {
          toast.success('Assignment created');
        }
      }
      setPaCatchAllConfirm({ open: false });
      closeModal();
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Save failed';
      toast.error(detail);
      setModalState((s) => ({ ...s, saving: false }));
    }
  };

  const handleConfirmCatchAll = async () => {
    setPaCatchAllConfirm({ open: false });
    if (modalState.draft) {
      await performSave(modalState.draft);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete.target) return;
    setConfirmDelete((s) => ({ ...s, submitting: true }));
    try {
      await deactivateJurisdictionAssignment(confirmDelete.target.id);
      toast.success('Assignment deactivated');
      setConfirmDelete({ open: false, target: null, submitting: false });
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Deactivate failed';
      toast.error(detail);
      setConfirmDelete((s) => ({ ...s, submitting: false }));
    }
  };

  const collectorById = useMemo(() => {
    const m = new Map();
    for (const c of collectors) m.set(c.id, c);
    return m;
  }, [collectors]);

  const categoryByCode = useMemo(() => {
    const m = new Map();
    for (const c of categories) m.set(c.code, c);
    return m;
  }, [categories]);

  // ── Empty state helper ────────────────────────────────────────
  const renderEmptyPeriod = (taxPeriod) => (
    <div
      className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-6 py-8 text-center"
      data-testid={`assignments-empty-${taxPeriod}`}
    >
      <p className="text-sm text-slate-600">
        No {taxPeriod} tax collector assignments configured.
      </p>
      <p className="mt-1 text-xs text-slate-500">
        {canWrite
          ? 'Add an assignment to override the legacy directory entry for PA.'
          : 'An admin or jurisdiction manager can add an assignment here.'}
      </p>
    </div>
  );

  // ── Single period table ───────────────────────────────────────
  const renderPeriodTable = (taxPeriod) => {
    const rows = byPeriod[taxPeriod] || [];
    return (
      <section
        className="rounded-md border border-slate-200 bg-white shadow-sm"
        data-testid={`assignments-section-${taxPeriod}`}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div>
            <h3 className="text-lg font-medium tracking-tight text-slate-900">
              {PERIOD_LABELS[taxPeriod]}
            </h3>
            <p className="text-xs text-slate-500">
              {rows.length} assignment{rows.length === 1 ? '' : 's'}
            </p>
          </div>
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => openAddModal(taxPeriod)}
              data-testid={`assignments-add-btn-${taxPeriod}`}
            >
              <Plus className="mr-1 h-4 w-4" /> Add
            </Button>
          )}
        </header>

        {rows.length === 0 ? (
          <div className="p-4">{renderEmptyPeriod(taxPeriod)}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="tf-thead">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Collector</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Category</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Scope</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Effective</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Priority</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {rows.map((a) => {
                  const coll = a.collector || collectorById.get(a.collector_id);
                  const cat  = a.tax_category_code
                    ? (categoryByCode.get(a.tax_category_code) || { display_name: a.tax_category_code, code: a.tax_category_code })
                    : null;
                  return (
                    <tr
                      key={a.id}
                      className={a.is_active ? '' : 'bg-slate-50/60'}
                      data-testid={`assignment-row-${a.id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">
                          {coll?.display_name || <span className="text-slate-400">Unknown collector</span>}
                        </div>
                        {coll?.collector_type && (
                          <div className="text-xs text-slate-500">{coll.collector_type.replaceAll('_', ' ')}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        {cat ? (
                          <span>
                            <span className="font-mono text-xs text-slate-500">{cat.code}</span>
                            <span className="ml-1.5">{cat.display_name}</span>
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">All categories</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700" data-testid={`assignment-scope-cell-${a.id}`}>
                        {a.scope_jurisdiction_id ? (
                          <span
                            className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700"
                            data-testid={`assignment-scope-pill-${a.id}`}
                            title="Applies only when the parcel is in this municipality"
                          >
                            {(a.scope && a.scope.jurisdiction_name) || 'Scoped'}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-500">All municipalities</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-700">
                        <div>{fmtDate(a.effective_from)}</div>
                        <div className="text-xs text-slate-500">
                          to {a.effective_to ? fmtDate(a.effective_to) : 'present'}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-slate-700">{a.priority ?? 0}</td>
                      <td className="px-3 py-2"><StatusPill active={a.is_active !== false} /></td>
                      <td className="px-3 py-2 text-right">
                        {canWrite && (
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openEditModal(a)}
                              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-blue-700"
                              title="Edit assignment"
                              data-testid={`assignment-edit-btn-${a.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            {a.is_active !== false && (
                              <button
                                type="button"
                                onClick={() => setConfirmDelete({ open: true, target: a, submitting: false })}
                                className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700"
                                title="Deactivate assignment"
                                data-testid={`assignment-delete-btn-${a.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    );
  };

  if (!jurisdictionId) {
    return (
      <div
        className="rounded-md border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600"
        data-testid="assignments-needs-save"
      >
        Save the jurisdiction first to manage collector assignments.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="jurisdiction-collector-assignments-tab">
      {/* Header / context */}
      <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3">
        <p className="text-sm text-slate-700">
          <span className="font-medium">Pennsylvania jurisdiction.</span>{' '}
          Manage multi-payee collector assignments for{' '}
          <span className="font-medium">{jurisdiction.jurisdiction_name}</span>.
        </p>
        <p className="mt-1 text-xs text-slate-500">
          Need a new collector? Open the{' '}
          <a
            href="/admin/jurisdictions/tax-collectors"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-0.5 text-blue-700 hover:underline"
            data-testid="assignments-open-collectors-link"
          >
            Tax Collectors directory <ExternalLink className="h-3 w-3" />
          </a>{' '}
          to create one first.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-slate-500" data-testid="assignments-loading">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading assignments…
        </div>
      ) : (
        <>
          {renderPeriodTable('current')}
          {renderPeriodTable('delinquent')}
        </>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={modalState.open} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent
          className="max-w-2xl"
          data-testid="assignment-edit-dialog"
        >
          <DialogHeader>
            <DialogTitle>
              {modalState.editing ? 'Edit Assignment' : `Add ${PERIOD_LABELS[modalState.draft?.tax_period] || ''} Assignment`}
            </DialogTitle>
            <DialogDescription>
              Link a tax collector to this jurisdiction for a specific tax period and optional category.
            </DialogDescription>
          </DialogHeader>

          {modalState.draft && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {/* Collector — COLLECTOR-PICKER-002 server-side searchable picker */}
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="assn-collector">Collector <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <button
                    type="button"
                    id="assn-collector"
                    data-testid="assignment-collector-select"
                    onClick={() => setPicker((p) => ({ ...p, open: !p.open }))}
                    className="flex h-9 w-full items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-left focus:outline-none focus:ring-2 focus:ring-slate-300"
                  >
                    <span className={selectedCollector ? 'text-slate-900 truncate' : 'text-slate-400'}>
                      {selectedCollector
                        ? `${selectedCollector.display_name}${selectedCollector.collector_type ? ` · ${selectedCollector.collector_type.replaceAll('_', ' ')}` : ''}${selectedCollector.is_active === false ? ' · (inactive)' : ''}`
                        : 'Select a tax collector'}
                    </span>
                    <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
                  </button>
                  {picker.open && (
                    <div className="absolute z-50 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg" data-testid="assignment-collector-dropdown">
                      <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
                        <Search className="h-4 w-4 text-slate-400 shrink-0" />
                        <input
                          autoFocus
                          value={picker.term}
                          onChange={(e) => setPicker((p) => ({ ...p, term: e.target.value }))}
                          placeholder="Search collectors (e.g. YORK COUNTY)…"
                          className="w-full text-sm outline-none placeholder:text-slate-400"
                          data-testid="assignment-collector-search"
                        />
                        {picker.searching && <Loader2 className="h-4 w-4 animate-spin text-slate-400 shrink-0" />}
                      </div>
                      <div className="max-h-64 overflow-y-auto py-1">
                        {picker.results.length === 0 && !picker.searching ? (
                          <div className="px-3 py-2 text-sm text-slate-500">
                            {picker.term ? `No PA collectors match "${picker.term}".` : 'No PA collectors. Add one in Tax Collectors first.'}
                          </div>
                        ) : (
                          picker.results.map((c) => {
                            const nationwide = !c.states_served || c.states_served.length === 0;
                            const selected = modalState.draft.collector_id === c.id;
                            return (
                              <button
                                key={c.id}
                                type="button"
                                data-testid={`assignment-collector-opt-${c.id}`}
                                onClick={() => {
                                  setSelectedCollector(c);
                                  setDraft({ collector_id: c.id });
                                  setPicker((p) => ({ ...p, open: false }));
                                }}
                                className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${selected ? 'bg-slate-50' : ''}`}
                              >
                                <Check className={`h-3.5 w-3.5 shrink-0 ${selected ? 'text-blue-600' : 'text-transparent'}`} />
                                <span className="truncate">
                                  {c.display_name}
                                  {c.collector_type ? ` · ${c.collector_type.replaceAll('_', ' ')}` : ''}
                                  {nationwide ? ' · (nationwide)' : ''}
                                  {c.is_active === false ? ' · (inactive)' : ''}
                                </span>
                              </button>
                            );
                          })
                        )}
                        {!picker.term && picker.total > picker.results.length && (
                          <div className="border-t border-slate-100 px-3 py-1.5 text-xs text-slate-400" data-testid="assignment-collector-more-hint">
                            Showing first {picker.results.length} of {picker.total} — type to search the full directory.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {modalState.errors.collector_id && (
                  <p className="text-xs text-red-600">{modalState.errors.collector_id}</p>
                )}
              </div>

              {/* Tax Period */}
              <div className="space-y-1.5">
                <Label htmlFor="assn-period">Tax Period <span className="text-red-500">*</span></Label>
                <Select
                  value={modalState.draft.tax_period}
                  onValueChange={(v) => setDraft({ tax_period: v })}
                >
                  <SelectTrigger id="assn-period" data-testid="assignment-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current</SelectItem>
                    <SelectItem value="delinquent">Delinquent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Tax Category */}
              <div className="space-y-1.5">
                <Label htmlFor="assn-category">Tax Category</Label>
                <Select
                  value={modalState.draft.tax_category_code || '__ALL__'}
                  onValueChange={(v) => setDraft({ tax_category_code: v === '__ALL__' ? '' : v })}
                >
                  <SelectTrigger id="assn-category" data-testid="assignment-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__ALL__">All categories (catch-all)</SelectItem>
                    {categories.map((c) => (
                      <SelectItem key={c.code} value={c.code} data-testid={`assignment-category-opt-${c.code}`}>
                        <span className="font-mono text-xs text-slate-500">{c.code}</span>
                        <span className="ml-2">{c.display_name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isPA ? (
                  <p
                    className="text-xs text-slate-600"
                    data-testid="assignment-category-pa-hint"
                  >
                    <strong>PA standard:</strong> pick an explicit category
                    (county / municipal / school / tax_claim / …). Use{' '}
                    <em>All categories</em> only when one collector genuinely
                    handles every tax type for this jurisdiction with no
                    category-specific carve-out.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">
                    Leave as “All categories” to catch every tax type for this period.
                  </p>
                )}
              </div>

              {/* Scope — Municipality-scoped override (Option C, Feb 2026).
                  Optional. When set, the assignment applies ONLY to parcels
                  located in the selected township/borough/etc. within the
                  same parent county. Scoped rows beat catch-all NULL-scope
                  rows in the resolver — see collector_resolver.py. */}
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="assn-scope">Scoped to Municipality</Label>
                <Select
                  value={modalState.draft.scope_jurisdiction_id || '__ALL__'}
                  onValueChange={(v) => setDraft({ scope_jurisdiction_id: v === '__ALL__' ? '' : v })}
                  disabled={loadingScopes || scopeCandidates.length === 0}
                >
                  <SelectTrigger id="assn-scope" data-testid="assignment-scope-select">
                    <SelectValue placeholder={loadingScopes
                      ? 'Loading municipalities…'
                      : scopeCandidates.length === 0
                        ? 'No eligible municipalities'
                        : 'All municipalities (catch-all)'}
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__ALL__" data-testid="assignment-scope-opt-all">
                      All municipalities (catch-all)
                    </SelectItem>
                    {scopeCandidates.map((s) => (
                      <SelectItem
                        key={s.id}
                        value={s.id}
                        data-testid={`assignment-scope-opt-${s.id}`}
                      >
                        {s.jurisdiction_name}
                        <span className="ml-2 text-xs text-slate-500">
                          · {(s.jurisdiction_type || '').replace('_', ' ')}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  Optional. Restrict this collector to parcels in a specific
                  township / borough / city / town / village within the same
                  parent county. Example: <em>Salem Township</em> under
                  Western Wayne School District so Laura Roos is used only
                  for Salem parcels.
                </p>
              </div>

              {/* Effective From / To */}
              <div className="space-y-1.5">
                <Label htmlFor="assn-eff-from">Effective From <span className="text-red-500">*</span></Label>
                <Input
                  id="assn-eff-from"
                  type="date"
                  value={modalState.draft.effective_from || ''}
                  onChange={(e) => setDraft({ effective_from: e.target.value })}
                  data-testid="assignment-effective-from-input"
                />
                {modalState.errors.effective_from && (
                  <p className="text-xs text-red-600">{modalState.errors.effective_from}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="assn-eff-to">Effective To</Label>
                <Input
                  id="assn-eff-to"
                  type="date"
                  value={modalState.draft.effective_to || ''}
                  onChange={(e) => setDraft({ effective_to: e.target.value })}
                  data-testid="assignment-effective-to-input"
                />
                {modalState.errors.effective_to && (
                  <p className="text-xs text-red-600 inline-flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {modalState.errors.effective_to}
                  </p>
                )}
                <p className="text-xs text-slate-500">Leave blank for open-ended (still active today).</p>
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <Label htmlFor="assn-priority">Priority (lower wins ties)</Label>
                <Input
                  id="assn-priority"
                  type="number"
                  value={modalState.draft.priority}
                  onChange={(e) => setDraft({ priority: e.target.value })}
                  data-testid="assignment-priority-input"
                />
                {modalState.errors.priority && (
                  <p className="text-xs text-red-600">{modalState.errors.priority}</p>
                )}
              </div>

              {/* Active toggle */}
              <div className="flex items-end gap-3">
                <div className="flex items-center gap-2">
                  <Switch
                    id="assn-active"
                    checked={!!modalState.draft.is_active}
                    onCheckedChange={(v) => setDraft({ is_active: v })}
                    data-testid="assignment-active-switch"
                  />
                  <Label htmlFor="assn-active">Active</Label>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-1.5 md:col-span-2">
                <Label htmlFor="assn-notes">Notes</Label>
                <Textarea
                  id="assn-notes"
                  rows={3}
                  value={modalState.draft.notes || ''}
                  onChange={(e) => setDraft({ notes: e.target.value })}
                  placeholder="Optional context — e.g. 'School district payee since FY2024 reorg.'"
                  data-testid="assignment-notes-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={closeModal}
              disabled={modalState.saving}
              data-testid="assignment-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={modalState.saving || !canWrite}
              data-testid="assignment-save-btn"
            >
              {modalState.saving ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…</>
              ) : (
                modalState.editing ? 'Update Assignment' : 'Create Assignment'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PA catch-all save guard — non-blocking nudge */}
      <AlertDialog
        open={paCatchAllConfirm.open}
        onOpenChange={(open) => { if (!open) setPaCatchAllConfirm({ open: false }); }}
      >
        <AlertDialogContent data-testid="assignment-pa-catchall-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Use “All categories” catch-all for this PA jurisdiction?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The PA operational standard is one explicit assignment per
              category (county / municipal / school / tax_claim / …). An
              explicit category resolves at <strong>high</strong> confidence
              and gives clean per-category telemetry. The “All categories”
              catch-all resolves at <strong>medium</strong> confidence and
              should be reserved for exceptional jurisdictions where one
              collector genuinely handles every tax type with no carve-out.
              <br /><br />
              Are you sure this is one of those exceptional cases?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="assignment-pa-catchall-cancel-btn"
            >
              Pick an explicit category
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmCatchAll}
              className="bg-amber-600 hover:bg-amber-700 focus:ring-amber-600"
              data-testid="assignment-pa-catchall-confirm-btn"
            >
              Yes, save as catch-all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Soft-delete confirmation */}
      <AlertDialog
        open={confirmDelete.open}
        onOpenChange={(open) => { if (!open) setConfirmDelete({ open: false, target: null, submitting: false }); }}
      >
        <AlertDialogContent data-testid="assignment-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate this assignment?</AlertDialogTitle>
            <AlertDialogDescription>
              The assignment will be marked inactive. Existing tax-certificate
              snapshots remain untouched, but the resolver will skip this row
              for new orders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={confirmDelete.submitting}
              data-testid="assignment-delete-cancel-btn"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={confirmDelete.submitting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
              data-testid="assignment-delete-confirm-btn"
            >
              {confirmDelete.submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deactivating…</>
              ) : 'Deactivate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default JurisdictionCollectorAssignmentsTab;
